import {
  SalesforceMappingOption,
  SalesforceResourceMappingLocalData,
} from 'module/api/integrations/salesforce/types';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PopoverSelect } from '@rocketlane/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { tv } from 'tailwind-variants';
import {
  buildFieldSelectGroups,
  renderFieldSelectTrigger,
  renderSalesforceFieldOptionIcon,
  resolveFieldSelectOption,
} from '../commons/field-select';

const emptyStateVariants = tv({
  slots: {
    container:
      'flex w-full items-center border-b border-[#f4f4f4] h-[40px] py-0.5',
    mainCell: 'flex items-center gap-2 px-2',
    syncCell: 'flex items-center gap-2 px-0',
    syncIcon: 'text-[#8d8d8d] text-[14px]',
    placeholderText:
      'text-[#0f62fe] opacity-50 text-[14px] font-normal leading-[18px] overflow-ellipsis overflow-hidden whitespace-nowrap',
  },
});

type EmptyStateProps = {
  fields: SalesforceMappingOption<boolean>[];
  onChange: (value: SalesforceMappingOption<boolean> | null) => void;
  fieldObject: SalesforceResourceMappingLocalData;
};

export const EmptyState = ({
  fields,
  onChange,
  fieldObject,
}: EmptyStateProps) => {
  const { container, mainCell, syncCell, syncIcon, placeholderText } =
    emptyStateVariants();
  const intl = useIntl();
  const groups = buildFieldSelectGroups(
    fields,
    fieldObject,
    intl.formatMessage({ id: 'fields' }),
  );

  return (
    <div className={container()}>
      <div className={mainCell()} style={{ flex: 1 }}>
        <span data-cy="pop.salesforce_field_value_add">
          <PopoverSelect
            collapsible={false}
            dropdownWidth={272}
            getOptionDataCy={(option) => `select-option-${option.label}`}
            groups={groups}
            maxListHeight={280}
            onChange={(selected) =>
              onChange(resolveFieldSelectOption(selected, fields))
            }
            renderOptionIcon={renderSalesforceFieldOptionIcon}
            renderTrigger={({ value }) =>
              renderFieldSelectTrigger({ type: 'salesforce', value })
            }
            searchPlaceholder={intl.formatMessage({
              id: 'placeholder.search',
            })}
            showFooter={false}
            value={null}
          />
        </span>
      </div>

      <div
        className={syncCell()}
        style={{
          width: '32px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <FontAwesomeIcon
          icon={faArrowRight}
          className={syncIcon()}
          style={{ color: 'var(--icon-secondary)' }}
        />
      </div>

      <div className={mainCell()} style={{ flex: 1 }}>
        <span className={placeholderText()}>
          <FormattedMessage id="settings.integrations.sf.resource_mappings.rocketlane.trigger" />
        </span>
      </div>

      <div className={mainCell()} style={{ flex: 1 }} />
    </div>
  );
};
